import { SubscriptionData, UserRole } from '@fsp/types';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { ArrowUpward } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cancelSubscription,
  fetchSubscription,
  reactivateSubscription,
} from '../../services/subscription';
import AddPaymentMethodForm from '../../components/AddPaymentMethodForm';
import PaperSection from '../../components/PaperSection';
import PlanSelector from '../../components/PlanSelector';
import PlanSelectorCoupon, { DiscountInfo } from '../../components/PlanSelectorCoupon';
import ContentWrapper from '../../layout/ContentWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { getOrganisationProfile } from '../../services';
import { useSnackbar } from 'notistack';

export const SubscriptionRoles = [UserRole.fsp, UserRole.OWNER, UserRole.ADMIN];

const Subscription: React.FC = () => {
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [needsPaymentMethod, setNeedsPaymentMethod] = useState<boolean>(false);
  const [promotionCode, setPromotionCode] = useState<string>('');
  const [orgPromotionCode, setOrgPromotionCode] = useState<string | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();
  const { organisationId } = useAuth();
  const navigate = useNavigate();

  // Load subscription data
  const loadSubscription = async () => {
    try {
      setError(null);
      const data = await fetchSubscription();
      setSubscriptionData(data);
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    }
  };

  const handleCancel = async () => {
    try {
      setActionLoading(true);
      await cancelSubscription();
      await loadSubscription();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
      enqueueSnackbar('Subscription canceled', { variant: 'info' });
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      await reactivateSubscription();
      await loadSubscription();
    } catch (err: any) {
      setError(err.message || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
      enqueueSnackbar('Subscription reactivated', { variant: 'success' });
    }
  };

  const handlePlanChanged = async () => {
    setNeedsPaymentMethod(false);
    await loadSubscription();
  };

  const handlePaymentMethodRequired = () => {
    setNeedsPaymentMethod(true);
  };

  const handleCardAddSuccess = async () => {
    setNeedsPaymentMethod(false);
    await loadSubscription();
  };

  const [discount, setDiscount] = useState<DiscountInfo | null>(null);

  const handlePromotionCodeApplied = (code: string, discountInfo: DiscountInfo | null) => {
    setPromotionCode(code);
    setDiscount(discountInfo);
  };

  // Load org's pre-attached promotion code (e.g. from affiliate signup)
  useEffect(() => {
    if (!organisationId) return;
    getOrganisationProfile(organisationId)
      .then((org) => {
        if (org.promotionCode) {
          setOrgPromotionCode(org.promotionCode);
        }
      })
      .catch(() => {
        // Non-critical — user can still enter code manually
      });
  }, [organisationId]);

  useEffect(() => {
    if (!organisationId) return;
    const init = async () => {
      setInitialLoading(true);
      await loadSubscription();
      setInitialLoading(false);
    };
    init();
  }, [organisationId]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateString: Date | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getCapitalizedCardBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const handleClose = () => {
    navigate('/settings/');
  };

  // if (initialLoading) {
  //   return (
  //     <ContentWrapper breadcrumb>
  //       <SubscriptionSkeleton />
  //       <PaperSection title="Available Plans">
  //         <PlanSelector currentSubscription={null} disabled />
  //       </PaperSection>
  //       <DangerZone />
  //     </ContentWrapper>
  //   );
  // }

  return (
    <ContentWrapper breadcrumb>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {initialLoading ? (
        <SubscriptionSkeleton />
      ) : (
        <PaperSection
          title="Your subscription"
          variant="default"
          action={
            <Stack direction="row" spacing={1}>
              <Button component="a" href="/settings/billing" disabled={actionLoading}>
                Edit Payment Card
              </Button>
              <IconButton onClick={handleClose}>
                <ArrowUpward />
              </IconButton>
            </Stack>
          }
        >
          {needsPaymentMethod ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No payment card
              </Typography>
              <Typography
                color="text.secondary"
                gutterBottom
                sx={{ maxWidth: 720, mx: 'auto', mb: 3 }}
              >
                You need to add a payment method before subscribing. Please add a card to continue.
              </Typography>
              <AddPaymentMethodForm onSuccess={handleCardAddSuccess} isFirstCard={true} />
            </Box>
          ) : !subscriptionData?.items ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No active subscription
              </Typography>
              <Typography
                color="text.secondary"
                gutterBottom
                sx={{ maxWidth: 720, mx: 'auto', mb: 3 }}
              >
                Choose a plan below to get started with Food Safety Plan's management tools.
              </Typography>
            </Box>
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h5">
                      {subscriptionData.product?.name || 'Subscription'}
                    </Typography>
                    <Chip
                      label={subscriptionData.subscription?.status || 'Unknown'}
                      color={getStatusColor(subscriptionData.subscription?.status || '')}
                      size="small"
                    />
                    {subscriptionData.subscription?.cancelAtPeriodEnd && (
                      <Chip
                        label={`Cancels on ${formatDate(subscriptionData.subscription.currentPeriodEnd)}`}
                        color="warning"
                        size="small"
                      />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    {subscriptionData.subscription?.status === 'canceled' ? (
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={
                          actionLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />
                        }
                        onClick={handleReactivate}
                        disabled={actionLoading}
                      >
                        Reactivate
                      </Button>
                    ) : subscriptionData.subscription?.cancelAtPeriodEnd ? (
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={
                          actionLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />
                        }
                        onClick={handleReactivate}
                        disabled={actionLoading}
                      >
                        Resume Subscription
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={actionLoading ? <CircularProgress size={20} /> : <CancelIcon />}
                        onClick={handleCancel}
                        disabled={actionLoading}
                      >
                        Cancel
                      </Button>
                    )}
                  </Stack>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Subscription
                    </Typography>
                    <Typography variant="h4" gutterBottom>
                      {subscriptionData.discount && subscriptionData.price?.amount ? (
                        <>
                          <Typography
                            component="span"
                            variant="h5"
                            color="text.secondary"
                            sx={{ textDecoration: 'line-through', mr: 1 }}
                          >
                            {formatCurrency(subscriptionData.price.amount, subscriptionData.price.currency || 'aud')}
                          </Typography>
                          <Typography component="span" variant="h4" color="success.main">
                            {formatCurrency(
                              subscriptionData.discount.percentOff
                                ? Math.round(subscriptionData.price.amount * (1 - subscriptionData.discount.percentOff / 100))
                                : subscriptionData.discount.amountOff
                                  ? Math.max(0, subscriptionData.price.amount - subscriptionData.discount.amountOff * 100)
                                  : subscriptionData.price.amount,
                              subscriptionData.price.currency || 'aud'
                            )}
                          </Typography>
                        </>
                      ) : (
                        formatCurrency(
                          subscriptionData.price?.amount || 0,
                          subscriptionData.price?.currency || 'aud'
                        )
                      )}
                      <Typography component="span" variant="body1" color="text.secondary">
                        /{subscriptionData.price?.interval || 'month'}
                      </Typography>
                    </Typography>
                    {subscriptionData.discount && (
                      <Typography variant="caption" color="text.secondary">
                        {subscriptionData.discount.duration === 'repeating' && subscriptionData.discount.durationInMonths
                          ? `Discount for ${subscriptionData.discount.durationInMonths} months, then ${formatCurrency(subscriptionData.price?.amount || 0, subscriptionData.price?.currency || 'aud')}/${subscriptionData.price?.interval || 'month'}`
                          : subscriptionData.discount.duration === 'once'
                            ? `Discount on first invoice, then ${formatCurrency(subscriptionData.price?.amount || 0, subscriptionData.price?.currency || 'aud')}/${subscriptionData.price?.interval || 'month'}`
                            : 'Discount applied forever'}
                      </Typography>
                    )}
                    {subscriptionData.product?.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {subscriptionData.product.description}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Stack spacing={1}>
                      {subscriptionData.subscription?.currentPeriodStart &&
                        subscriptionData.subscription?.currentPeriodEnd && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarTodayIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              Current period:{' '}
                              {formatDate(subscriptionData.subscription.currentPeriodStart)} -{' '}
                              {formatDate(subscriptionData.subscription.currentPeriodEnd)}
                            </Typography>
                          </Box>
                        )}

                      {subscriptionData.subscription?.trialEnd && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Trial ends: {formatDate(subscriptionData.subscription.trialEnd)}
                          </Typography>
                        </Box>
                      )}

                      {subscriptionData.paymentMethod?.card && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CreditCardIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {getCapitalizedCardBrand(
                              subscriptionData.paymentMethod.card.brand || ''
                            )}
                            {subscriptionData.paymentMethod.card.last4 &&
                              ` ending in ${subscriptionData.paymentMethod.card.last4}`}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {subscriptionData.latestInvoice && (
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Latest Invoice
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ReceiptIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatCurrency(
                              subscriptionData.latestInvoice.total || 0,
                              subscriptionData.latestInvoice.currency || 'aud'
                            )}{' '}
                            - {subscriptionData.latestInvoice.status || 'Unknown'}
                          </Typography>
                        </Box>
                        {subscriptionData.latestInvoice.hostedInvoiceUrl && (
                          <Button
                            size="small"
                            variant="outlined"
                            href={subscriptionData.latestInvoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ mt: 1, alignSelf: 'flex-start' }}
                            disabled={actionLoading}
                          >
                            View Invoice
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </PaperSection>
      )}

      {/* Available plans */}
      <PaperSection title="Available Plans" variant="default">
        <PlanSelector
          currentSubscription={subscriptionData}
          onPlanChanged={handlePlanChanged}
          onPaymentMethodRequired={handlePaymentMethodRequired}
          disabled={actionLoading}
          promotionCode={promotionCode}
          discount={discount}
        />
        {!subscriptionData?.items && (
          <PlanSelectorCoupon
            onPromotionCodeApplied={handlePromotionCodeApplied}
            initialCode={orgPromotionCode}
          />
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

export default Subscription;

const SubscriptionSkeleton: React.FC = () => {
  return (
    <PaperSection
      title="Your subscription"
      variant="default"
      action={
        <Button component="a" href="/settings/billing" disabled>
          Edit Payment Card
        </Button>
      }
    >
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Skeleton variant="text" width={150} height={32} />
              <Skeleton variant="rectangular" width={80} height={24} />
            </Stack>
            <Skeleton variant="rectangular" width={120} height={36} />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={180} height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="90%" height={20} />
              <Skeleton variant="text" width="80%" height={20} />
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="text" width="90%" height={20} />
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="text" width="90%" height={20} />
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="text" width="90%" height={20} />
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
              <Box display="flex" alignItems="center" gap={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="80%" height={20} />
              </Box>
              <Skeleton variant="rectangular" width={120} height={36} sx={{ mt: 1 }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </PaperSection>
  );
};
